import * as React from 'react';
import { useState, useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useRouter } from 'found';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCertificate } from '@fortawesome/pro-regular-svg-icons';
import Card from '../../../sharedComponents/core/Cards/Card';
import CardPagination from '../ActivityCard/CardPagination';
import { Theme } from 'helpers/theme';

const ACTIVE_COLOUR = '#3e55f6';
const EXPIRED_COLOUR = '#7b8ff7';

const ELEVATED_SHADOW =
  '0 70px 20px 0 rgba(209, 209, 209, 0.00), 0 45px 18px 0 rgba(209, 209, 209, 0.01), 0 25px 15px 0 rgba(209, 209, 209, 0.05), 0 11px 11px 0 rgba(209, 209, 209, 0.09), 0 3px 6px 0 rgba(209, 209, 209, 0.10)';

const useStyles = createUseStyles((theme: Theme) => ({
  cardRoot: {
    height: '100%',
    boxShadow: ELEVATED_SHADOW
  },
  card: {
    padding: '22px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '100%',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  iconContainer: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: '#F4F5F7',
    borderRadius: 8,
    fontSize: 14
  },
  titleBlock: {},
  title: {
    fontWeight: 800,
    fontSize: 14,
    color: theme.colors.primaryBlack
  },
  description: {
    fontSize: 12,
    color: '#6A728F',
    marginTop: 2
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 2fr) 56px',
    alignItems: 'center',
    gap: '14px',
    padding: '4px 0',
    '&:hover $rowName': {
      color: theme.colors.primaryBlack
    }
  },
  rowName: {
    fontSize: 13,
    fontWeight: 600,
    color: '#3A3F58',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s'
  },
  barTrack: {
    position: 'relative',
    height: 13,
    display: 'flex',
    gap: '3px'
  },
  barActive: {
    background: ACTIVE_COLOUR,
    height: '100%',
    borderRadius: 5,
    cursor: 'pointer',
    transition: 'width 0.6s ease, box-shadow 0.15s ease',
    '&:hover': {
      boxShadow: 'inset 0 0 0 2.5px rgba(0, 0, 0, 0.35)'
    }
  },
  barExpired: {
    background: EXPIRED_COLOUR,
    height: '100%',
    borderRadius: 5,
    cursor: 'pointer',
    transition: 'width 0.6s ease, box-shadow 0.15s ease',
    '&:hover': {
      boxShadow: 'inset 0 0 0 2.5px rgba(0, 0, 0, 0.35)'
    }
  },
  rowCounts: {
    fontSize: 12,
    color: '#6A728F',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    textAlign: 'right'
  },
  rowCountActive: {
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  bottom: {
    marginTop: 'auto'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 4
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: '28px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: 11,
    color: '#6A728F'
  },
  legendDot: {
    width: 14,
    height: 14,
    borderRadius: 4
  },
  totals: {
    fontSize: 11,
    color: '#6A728F'
  },
  empty: {
    padding: 20,
    color: '#6a728f',
    fontSize: 14
  }
}));

type CategoryStatus = {
  secondaryCategory: { name: string | null; uuid: string | null } | null;
  totals: {
    available: number;
    expired: number;
    expiringSoon: number;
    incomplete: number;
  };
};

type Props = {
  categories: ReadonlyArray<CategoryStatus>;
  className?: string;
};

export default function CertificateCategoriesCard({
  categories,
  className
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { router } = useRouter();
  const pageSize = 6;
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setVisible(true);
  }, []);

  const rows = categories.map((status) => {
    const active = status.totals.available;
    const expired = status.totals.expired + status.totals.incomplete;
    const total = active + expired;
    return {
      name: status.secondaryCategory?.name ?? 'Certificates',
      uuid:
        status.secondaryCategory?.uuid ??
        '00000000-0000-0000-0000-000000000000',
      active,
      expired,
      total
    };
  });

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  const visibleRows = rows.slice(start, start + pageSize);

  const totalActive = rows.reduce((sum, r) => sum + r.active, 0);
  const totalExpired = rows.reduce((sum, r) => sum + r.expired, 0);
  const grandTotal = totalActive + totalExpired;
  const overallPct =
    grandTotal > 0 ? Math.round((totalActive / grandTotal) * 100) : 0;

  return (
    <Card
      className={`${classes.cardRoot}${className ? ` ${className}` : ''}`}
      padding="none"
    >
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={classes.iconContainer}>
            <FontAwesomeIcon icon={faFileCertificate} color="#6A728F" />
          </div>
          <div className={classes.titleBlock}>
            <div className={classes.title}>Certificates</div>
            <div className={classes.description}>
              Active and expired certificates
            </div>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className={classes.empty}>No certificate categories yet</div>
        ) : (
          <>
            <div className={classes.rows}>
              {visibleRows.map((row, i) => {
                const activePct =
                  row.total > 0 ? (row.active / row.total) * 100 : 0;
                const expiredPct =
                  row.total > 0 ? (row.expired / row.total) * 100 : 0;
                return (
                  <div key={start + i} className={classes.row} title={row.name}>
                    <div className={classes.rowName}>{row.name}</div>
                    <div className={classes.barTrack}>
                      {activePct > 0 && (
                        <div
                          className={classes.barActive}
                          style={{ width: visible ? `${activePct}%` : 0 }}
                          title={`${row.name}: view ${row.active} active`}
                          onClick={() =>
                            router.push(
                              `/app/delegates?secondaryCategory=${row.uuid}&hasActiveCert=true`
                            )
                          }
                        />
                      )}
                      {expiredPct > 0 && (
                        <div
                          className={classes.barExpired}
                          style={{ width: visible ? `${expiredPct}%` : 0 }}
                          title={`${row.name}: view ${row.expired} expired`}
                          onClick={() =>
                            router.push(
                              `/app/delegates?secondaryCategory=${row.uuid}&hasExpiredCert=true`
                            )
                          }
                        />
                      )}
                    </div>
                    <div className={classes.rowCounts}>
                      <span className={classes.rowCountActive}>
                        {row.active}
                      </span>
                      <span> / {row.total}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={classes.bottom}>
              <CardPagination
                page={safePage}
                pageSize={pageSize}
                totalItems={rows.length}
                onChange={setPage}
              />
              <div className={classes.footer}>
                <div className={classes.legend}>
                  <div className={classes.legendItem}>
                    <div
                      className={classes.legendDot}
                      style={{ background: ACTIVE_COLOUR }}
                    />
                    Active
                  </div>
                  <div className={classes.legendItem}>
                    <div
                      className={classes.legendDot}
                      style={{ background: EXPIRED_COLOUR }}
                    />
                    Expired
                  </div>
                </div>
                <div className={classes.totals}>
                  {totalActive} active · {totalExpired} expired
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
